import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, RefreshCw, Inbox as InboxIcon, MessageSquare, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { inboxApi, inboxLabels, formatRelative } from "../lib/inboxApi";
import {
  crisisNotifyPref, notificationsSupported, notificationStatus,
  requestNotificationPermission, fireCrisisNotification,
} from "../lib/crisisNotify";

const POLL_INTERVAL = 30_000;
const STATUS_OPTIONS = ["novo", "andamento", "respondido", "escalado"];
const TYPE_OPTIONS = ["DUVIDA", "ORACAO", "CRISE", "GERAL"];

export default function AdminInboxTab() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const prevEscaladoRef = useRef(null);
  const [notifyOn, setNotifyOn] = useState(() =>
    crisisNotifyPref.get() === "on" && notificationStatus() === "granted"
  );

  const fetchData = useCallback(async (opts = {}) => {
    const params = {};
    if (filterStatus) params.status = filterStatus;
    if (filterType) params.type = filterType;
    params.limit = 60;
    try {
      if (opts.silent) setRefreshing(true);
      else setLoading(true);
      const [list, c] = await Promise.all([
        inboxApi.list(params),
        inboxApi.counts(),
      ]);
      // Crisis delta detection, fire desktop notification if user opted in.
      if (
        notifyOn &&
        prevEscaladoRef.current !== null &&
        c.escalado > prevEscaladoRef.current
      ) {
        fireCrisisNotification(c.escalado - prevEscaladoRef.current);
      }
      prevEscaladoRef.current = c.escalado;
      setItems(list.items);
      setCounts(c);
      setLastFetch(new Date());
    } catch (err) {
      if (!opts.silent) toast.error(err?.response?.data?.detail || "Falha ao carregar inbox.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterStatus, filterType, notifyOn]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const id = window.setInterval(() => fetchData({ silent: true }), POLL_INTERVAL);
    return () => window.clearInterval(id);
  }, [fetchData]);

  const empty = !loading && items.length === 0;

  return (
    <div data-testid="admin-inbox-tab" className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Módulo 2</p>
          <h2 className="font-display text-2xl text-navy mt-1">Inbox Pastoral</h2>
          {lastFetch && (
            <p className="text-[11px] text-navy/45 mt-1">
              Atualizado {formatRelative(lastFetch.toISOString())} · auto a cada 30s
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <CrisisNotifyToggle on={notifyOn} setOn={setNotifyOn} />
          <button
            type="button"
            onClick={() => fetchData()}
            disabled={loading || refreshing}
            data-testid="inbox-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-[11px] uppercase tracking-widest text-navy/75 hover:border-gold/60 transition-colors disabled:opacity-50"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Atualizar
          </button>
        </div>
      </header>

      <CountsBar counts={counts} active={filterStatus} onPick={setFilterStatus} />

      <TypeFilter value={filterType} onChange={setFilterType} />

      {loading ? (
        <div className="text-center text-navy/55 py-12" data-testid="inbox-loading">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Carregando…
        </div>
      ) : empty ? (
        <div className="text-center text-navy/55 py-12 bg-white border border-border rounded-2xl" data-testid="inbox-empty">
          <InboxIcon className="w-7 h-7 mx-auto mb-2 text-navy/30" />
          <p className="text-sm">Nenhuma interação para os filtros atuais.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="inbox-list">
          {items.map((it) => (<InboxCard key={it.id} item={it} />))}
        </ul>
      )}
    </div>
  );
}

function CountsBar({ counts, active, onPick }) {
  if (!counts) return null;
  const items = [
    { key: "", label: "Todos", n: counts.total, tone: "bg-navy text-white" },
    { key: "novo", label: "Novo", n: counts.novo, tone: "bg-amber-100 text-amber-800" },
    { key: "andamento", label: "Em andamento", n: counts.andamento, tone: "bg-sky-100 text-sky-800" },
    { key: "respondido", label: "Respondido", n: counts.respondido, tone: "bg-emerald-100 text-emerald-800" },
    { key: "escalado", label: "Escalado", n: counts.escalado, tone: "bg-rose-100 text-rose-800" },
  ];
  return (
    <div className="flex flex-wrap gap-2" data-testid="inbox-counts">
      {items.map((b) => {
        const isActive = (active || "") === b.key;
        return (
          <button
            key={b.key || "todos"}
            type="button"
            onClick={() => onPick(b.key)}
            data-testid={`inbox-filter-status-${b.key || "todos"}`}
            data-active={isActive ? "true" : "false"}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest transition-colors ${
              isActive ? "bg-navy text-white" : "bg-[hsl(var(--parchment))] border border-border text-navy/75 hover:border-gold/60"
            }`}
          >
            {b.label}
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${isActive ? "bg-white/20" : b.tone}`}>
              {b.n}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function TypeFilter({ value, onChange }) {
  const opts = useMemo(() => [
    { k: "", lab: "Todos os tipos" },
    ...TYPE_OPTIONS.map((t) => ({ k: t, lab: inboxLabels.TYPE_LABEL[t] })),
  ], []);
  return (
    <div className="flex flex-wrap gap-2" data-testid="inbox-type-filter">
      {opts.map((o) => {
        const isActive = (value || "") === o.k;
        return (
          <button
            key={o.k || "all"}
            type="button"
            onClick={() => onChange(o.k)}
            data-testid={`inbox-filter-type-${o.k || "all"}`}
            data-active={isActive ? "true" : "false"}
            className={`text-[11px] px-3 py-1 rounded-full border transition-colors ${
              isActive
                ? `${inboxLabels.TYPE_TONE[o.k] || "bg-navy text-white border-navy"}`
                : "bg-white border-border text-navy/65 hover:border-gold/60"
            }`}
          >
            {o.lab}
          </button>
        );
      })}
    </div>
  );
}

function CrisisNotifyToggle({ on, setOn }) {
  if (!notificationsSupported()) return null;

  const handleToggle = async () => {
    if (on) {
      crisisNotifyPref.set("off");
      setOn(false);
      toast.info("Alertas de crise desativados.");
      return;
    }
    const status = await requestNotificationPermission();
    if (status === "granted") {
      crisisNotifyPref.set("on");
      setOn(true);
      toast.success("Alertas de crise ativados. Você será notificado em tempo real.");
    } else if (status === "denied") {
      toast.error("Permissão negada. Ajuste nas configurações do navegador.");
    } else {
      toast.info("Permissão não concedida.");
    }
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      data-testid="crisis-notify-toggle"
      data-state={on ? "on" : "off"}
      title={on ? "Desativar alertas em tempo real" : "Ativar alertas em tempo real"}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors ${
        on
          ? "bg-rose-500/15 text-rose-700 border border-rose-500/40 hover:bg-rose-500/20"
          : "border border-border text-navy/75 hover:border-gold/60"
      }`}
    >
      {on ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
      Alertas
    </button>
  );
}

function InboxCard({ item }) {
  const isCrisis = item.escalate || item.type === "CRISE";
  const tTone = inboxLabels.TYPE_TONE[item.type] || inboxLabels.TYPE_TONE.GERAL;
  const sTone = inboxLabels.STATUS_TONE[item.status] || "bg-slate-100 text-slate-700";
  return (
    <li
      data-testid={`inbox-card-${item.id}`}
      className={`bg-white rounded-2xl p-4 transition-shadow hover:shadow-sm ${
        isCrisis ? "border-2 border-rose-500/50" : "border border-border"
      }`}
    >
      <Link
        to={`/admin/inbox/${item.id}`}
        className="flex items-start gap-3"
        data-testid={`inbox-card-link-${item.id}`}
      >
        {isCrisis && (
          <span className="shrink-0 w-7 h-7 rounded-full bg-rose-500/15 text-rose-600 flex items-center justify-center" aria-hidden>
            <AlertTriangle className="w-3.5 h-3.5" />
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="font-medium text-navy text-sm truncate">{item.user_name}</p>
            <span className="text-[10px] text-navy/45 shrink-0">{formatRelative(item.created_at)}</span>
          </div>
          <p className="text-[11px] text-navy/55 mb-2">{item.user_phone}</p>
          <p className="text-sm text-navy/85 line-clamp-2">{item.message}</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-widest ${tTone}`}>
              {inboxLabels.TYPE_LABEL[item.type]}
            </span>
            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${sTone}`}>
              {inboxLabels.STATUS_LABEL[item.status]}
            </span>
            {item.prayer_registered && (
              <span className="text-[10px] text-emerald-700">oração registrada</span>
            )}
            {item.nota_interna && (
              <span className="inline-flex items-center gap-1 text-[10px] text-navy/55">
                <MessageSquare className="w-3 h-3" /> nota
              </span>
            )}
          </div>
        </div>
      </Link>
    </li>
  );
}
